import { createSelector } from 'reselect';
import { createReducer } from 'typesafe-actions';

import { actions, RootAction } from '../../actions';
import { OverviewViewMode, OverviewViewModeStrings, Store, UserSystemGroup } from '../../store/storeTypes';
import { MainReportTypes, SubReportTypes } from '../../models/ReportData';
import { activeReportSelector } from '../reports/filterDataReducer';
import {
  pbiReportHeightSelector,
  pbiReportWidthSelector,
  screenWidthSelector,
} from '../appUserReducer';
import { systemGroupSelector } from './systemGroupUiReducer';

const INIT_STATE: OverviewViewModeStrings = OverviewViewMode.TwentyFourHours;

const { setTimeViewMode } = actions.userUi;
const { getProductionHoursMatrixAC } = actions.productionHoursApi;

export default createReducer<OverviewViewModeStrings, RootAction>(INIT_STATE)
  .handleAction(setTimeViewMode, (state, action) => action.payload)
  // Production view is unavailable without any configured production hours (the toggle disables it too)
  .handleAction(getProductionHoursMatrixAC.success, (state, action) =>
    state === OverviewViewMode.Production && !action.payload?.entries.some(entry => entry.isSet)
      ? OverviewViewMode.TwentyFourHours
      : state
  )
  .handleAction(getProductionHoursMatrixAC.failure, () => OverviewViewMode.TwentyFourHours);

//*selectors
const uiSelector = (state: Store) => {
  return state.ui;
};
export const timeViewModeSelector = createSelector([uiSelector], ui => {
  return ui['timeViewMode'];
});

// Same rule Overview.tsx uses to derive its own isProductionMode - kept here so any
// other consumer (e.g. the machine filter tree) can check it without duplicating the logic.
export const isOverviewProductionModeSelector = createSelector(
  [timeViewModeSelector, systemGroupSelector],
  (timeViewMode, selectedSystemGroup) =>
    timeViewMode === OverviewViewMode.Production && selectedSystemGroup !== UserSystemGroup.DTF
);

// Both the saved default (user settings) and the production-hours matrix must be settled before
// reports request data - otherwise the first request can go out in a view mode that is about to flip.
export const isTimeViewModeResolvedSelector = (state: Store) => {
  const { userSettings, productionHoursApi } = state;
  const isSettingsSettled = !!userSettings.isLoaded || userSettings.isError;
  const isMatrixSettled = !!productionHoursApi.matrix || productionHoursApi.isMatrixError;
  return isSettingsSettled && isMatrixSettled;
};

export const isDtfOnlyUser = (userSystemGroups?: string[]) =>
  !!userSystemGroups?.length && userSystemGroups.every(group => group === UserSystemGroup.DTF);

// Mirrors the desktop/mobile switch of each page (Overview.tsx, SystemPage.tsx, SystemsAnalysis.tsx)
const isMobileLayout = (
  activeReport: string,
  screenWidth: number,
  pbiReportWidth: number,
  pbiReportHeight: number
) => {
  if (activeReport === MainReportTypes.Systems) {
    return screenWidth <= 1280;
  }
  if (activeReport === SubReportTypes.SystemPage) {
    return pbiReportWidth < 1366 || pbiReportHeight < 545;
  }
  return pbiReportWidth < 1366 || pbiReportHeight < 567;
};

export const isMobileLayoutSelector = createSelector(
  [activeReportSelector, screenWidthSelector, pbiReportWidthSelector, pbiReportHeightSelector],
  isMobileLayout
);

export const isTimeViewModeToggleVisibleSelector = createSelector(
  [systemGroupSelector, activeReportSelector, isMobileLayoutSelector],
  (selectedSystemGroup, activeReport, isMobile) =>
    selectedSystemGroup !== UserSystemGroup.DTF && activeReport !== MainReportTypes.Jobs && !isMobile
);
